/**
 * @file     listingSpecs.ts
 * @purpose  Pure field→spec-card derivation for ListingSpecsGrid. No JSX/MUI so
 *           the visibility/value logic (the part that silently dropped fields
 *           when threaded as scalar props) is unit-testable in the node env.
 */

import type { ListingDetail } from '@/services/mls/mlsPropertyService';
import type { VowTeaserField } from '@/services/mls/vowOnlyFields';
import { formatLotSize } from '@/utils/formatLotSize';
import { formatCADCents } from '@/utils/finance/mortgage';
import { formatLongDate } from '@/utils/dateFormat';

/** A stat card. `iconKey` is resolved to an SVG by the rendering component. */
export interface SpecItem {
  iconKey: string;
  value: string;
  label: string;
}

export interface DetailItem {
  label: string;
  value: string;
  /** VOW-only and withheld from this caller: draw a placeholder, never a value. */
  locked?: boolean;
  /** The withheld field, on locked items (analytics names it). */
  field?: VowTeaserField;
}

/** True when the server withheld this VOW-only field and said the listing has it. */
export function isVowLocked(l: ListingDetail, field: VowTeaserField): boolean {
  return l.vowFieldsPresent?.includes(field) === true;
}

export interface AmenityItem {
  label: string;
  iconKey: string;
}

export function bathLabel(full: number | null, half: number | null): string | null {
  return (
    [
      full != null && `${full} full`,
      half != null && half > 0 && `${half} half`,
    ]
      .filter(Boolean)
      .join(' + ') || (full != null ? String(full) : null)
  );
}

export function petAllowanceLabel(values: string[] | null): string | null {
  if (!values || values.length === 0) return null;
  const joined = values.join(' ').toLowerCase();
  if (/not allowed|no pets|^no$/.test(joined)) return 'Not allowed';
  const types = [
    /dog/.test(joined) && 'Dogs',
    /cat/.test(joined) && 'Cats',
  ].filter(Boolean) as string[];
  if (types.length > 0) return types.join(' & ');
  if (/restriction/.test(joined)) return 'Allowed';
  if (/yes|allowed|permitted|ok/.test(joined)) return 'Allowed';
  return values[0];
}

export function typeIconKey(subType: string | null, type: string): string {
  const s = (subType ?? type).toLowerCase();
  if (s.includes('apartment') || s.includes('condo')) return 'condo';
  if (s.includes('townhouse')) return 'townhouse';
  if (s.includes('single family') || s.includes('house') || s.includes('detached')) return 'house';
  if (s.includes('duplex') || s.includes('half')) return 'duplex';
  if (s.includes('land') || s.includes('lot')) return 'land';
  if (s.includes('manufactured') || s.includes('mobile')) return 'manufactured';
  if (s.includes('lease') || s.includes('rental')) return 'rent';
  if (s.includes('income') || s.includes('multi')) return 'income';
  return 'townhouse';
}

/** Row 1 — financial + size facts. Untrimmed; caller trims for layout. */
export function buildPropertyFacts(l: ListingDetail): SpecItem[] {
  const baths = bathLabel(l.bathroomsFull, l.bathroomsHalf);
  const pets = petAllowanceLabel(l.petsAllowed);
  const petsRestricted = l.petsAllowed?.some((value) => /restriction/i.test(value)) ?? false;
  return [
    l.bedroomsTotal != null && { iconKey: 'bed', value: String(l.bedroomsTotal), label: 'Beds' },
    baths && { iconKey: 'bath', value: baths, label: 'Baths' },
    l.livingArea != null &&
      l.livingArea > 0 && {
        iconKey: 'sqft',
        value: `${Math.round(l.livingArea).toLocaleString('en-CA')} ft²`,
        label: 'Floor area',
      },
    l.lotSizeSquareFeet != null &&
      l.lotSizeSquareFeet > 0 && {
        iconKey: 'lot',
        value: formatLotSize(l.lotSizeSquareFeet)!,
        label: 'Lot size',
      },
    l.yearBuilt != null && { iconKey: 'cal', value: String(l.yearBuilt), label: 'Year built' },
    pets && { iconKey: 'pets', value: pets, label: petsRestricted ? 'Pets · restrictions' : 'Pets' },
    l.stories != null &&
      l.stories > 0 && {
        iconKey: 'stairs',
        value: String(l.stories),
        label: l.stories === 1 ? 'Storey' : 'Storeys',
      },
    l.parkingTotal != null &&
      l.parkingTotal > 0 && { iconKey: 'car', value: String(l.parkingTotal), label: 'Parking' },
    l.associationFee != null &&
      l.associationFee > 0 && {
        iconKey: 'strata',
        value: `${formatCADCents(l.associationFee)}/mo`,
        label: 'Strata fee',
      },
    l.taxAnnualAmount != null &&
      l.taxAnnualAmount > 0 && {
        iconKey: 'tax',
        value: `${formatCADCents(l.taxAnnualAmount)}/yr`,
        label: 'Gross tax',
      },
  ].filter(Boolean) as SpecItem[];
}

/** Row 2 — building & location character facts. Untrimmed. */
export function buildLocationFacts(l: ListingDetail): SpecItem[] {
  return [
    (l.propertySubType || l.propertyType) && {
      iconKey: `type:${typeIconKey(l.propertySubType, l.propertyType)}`,
      value: l.propertySubType ?? l.propertyType,
      label: 'Type',
    },
    l.basement != null &&
      l.basement.length > 0 && { iconKey: 'basement', value: l.basement[0], label: 'Basement' },
    l.garageYn != null && { iconKey: 'garage', value: l.garageYn ? 'Yes' : 'No', label: 'Garage' },
    l.carportYn != null && {
      iconKey: 'carport',
      value: l.carportYn ? 'Yes' : 'No',
      label: 'Carport',
    },
    l.fireplacesTotal != null &&
      l.fireplacesTotal > 0 && {
        iconKey: 'fireplace',
        value: String(l.fireplacesTotal),
        label: l.fireplacesTotal === 1 ? 'Fireplace' : 'Fireplaces',
      },
    l.mlsAreaMajor && { iconKey: 'mappin', value: l.mlsAreaMajor, label: 'Area' },
    l.mlsAreaMinor && { iconKey: 'mappin', value: l.mlsAreaMinor, label: 'Sub-area' },
    l.architecturalStyle != null &&
      l.architecturalStyle.length > 0 && {
        iconKey: 'style',
        value: l.architecturalStyle[0],
        label: 'Style',
      },
  ].filter(Boolean) as SpecItem[];
}

/**
 * Compact "key facts" for the collapsed specs grid — up to 6 items. Excludes
 * anything already in the hero (beds, baths, floor area, price, type) so the
 * grid adds information instead of repeating it. Backfills from the next most
 * useful available facts when a primary fact is absent.
 */
export function buildKeyFacts(l: ListingDetail): SpecItem[] {
  const byLabel = new Map<string, SpecItem>();
  for (const item of [...buildPropertyFacts(l), ...buildLocationFacts(l)]) {
    if (!byLabel.has(item.label)) byLabel.set(item.label, item);
  }
  const priority = [
    'Parking',
    'Year built',
    'Strata fee',
    'Gross tax',
    'Lot size',
    'Basement',
    // Backfill order when a primary fact is absent.
    'Storey',
    'Storeys',
    'Garage',
    'Fireplace',
    'Fireplaces',
    'Style',
    'Carport',
    'Area',
    'Sub-area',
  ];
  const out: SpecItem[] = [];
  for (const label of priority) {
    const item = byLabel.get(label);
    if (item) out.push(item);
    if (out.length === 6) break;
  }
  return out;
}

const GENERIC_STYLES = new Set(['other', 'ground level unit', '']);

/**
 * Static "listed on" date — e.g. "May 13, 2026". Deliberately a fixed date, never
 * an elapsed-day count: BCRES forbids days-on-market on IDX surfaces.
 */
export function formatListedDate(iso: string | null): string | null {
  if (!iso) return null;
  return formatLongDate(iso) || null;
}

/** Details strip — listed date, style, basement, structure, frontage, PID. */
export function buildDetailStrip(l: ListingDetail): DetailItem[] {
  const style = l.architecturalStyle?.find((s) => !GENERIC_STYLES.has(s.toLowerCase())) ?? null;
  const basementVal = l.basement && l.basement.length > 0 ? l.basement[0] : null;
  const structure = l.structureType && l.structureType.length > 0 ? l.structureType[0] : null;
  const frontage =
    l.frontageLength != null && l.frontageLength > 0
      ? `${l.frontageLength} ${l.frontageLengthUnits ?? 'ft'}`
      : null;
  return [
    formatListedDate(l.listedDate)
      ? { label: 'Listed', value: formatListedDate(l.listedDate) as string }
      : isVowLocked(l, 'listedDate') && { label: 'Listed', value: '', locked: true, field: 'listedDate' as const },
    style && { label: 'Style', value: style },
    basementVal && { label: 'Basement', value: basementVal },
    structure && { label: 'Structure', value: structure },
    frontage && { label: 'Frontage', value: frontage },
    l.parcelNumber
      ? { label: 'PID', value: l.parcelNumber }
      : isVowLocked(l, 'parcelNumber') && { label: 'PID', value: '', locked: true, field: 'parcelNumber' as const },
  ].filter(Boolean) as DetailItem[];
}

export interface FeatureGroup {
  label: string;
  values: string[];
  /** VOW-only and withheld from this caller (values is empty). */
  locked?: boolean;
  /** The withheld field, on locked groups. */
  field?: VowTeaserField;
}

/**
 * Labelled inclusion lists (Appliances, Cooling, …) shown as a structured strip
 * — the realtor.ca "Property Summary" parity. Distinct from the curated
 * `extractedFeatures` highlight pills: these are the raw RESO arrays.
 */
export function buildFeatureGroups(l: ListingDetail): FeatureGroup[] {
  return (
    [
      { label: 'Appliances', values: l.appliances },
      { label: 'Heating', values: l.heating },
      { label: 'Cooling', values: l.cooling },
      { label: 'Flooring', values: l.flooring, vow: 'flooring' },
      { label: 'Construction', values: l.constructionMaterials, vow: 'constructionMaterials' },
      { label: 'Roof', values: l.roof, vow: 'roof' },
      { label: 'Parking', values: l.parkingFeatures },
      { label: 'Lot', values: l.lotFeatures },
      { label: 'Interior features', values: l.interiorFeatures },
      { label: 'Laundry', values: l.laundryFeatures },
      { label: 'Community', values: l.communityFeatures },
    ] as Array<{ label: string; values: string[] | null; vow?: VowTeaserField }>
  )
    .map((g) => {
      const values = (g.values ?? []).filter((v) => v.length > 0);
      return values.length === 0 && g.vow && isVowLocked(l, g.vow)
        ? { label: g.label, values, locked: true, field: g.vow }
        : { label: g.label, values };
    })
    .filter((g) => g.values.length > 0 || g.locked === true);
}

/** CREA/BCRES amenity vocabulary → display label + icon key. */
export const AMENITY_MAP: Record<string, AmenityItem> = {
  'exercise centre': { label: 'Gym', iconKey: 'gym' },
  'exercise center': { label: 'Gym', iconKey: 'gym' },
  gym: { label: 'Gym', iconKey: 'gym' },
  elevator: { label: 'Elevator', iconKey: 'elev' },
  'in suite laundry': { label: 'In-suite laundry', iconKey: 'wash' },
  'air cond./central': { label: 'Air conditioning', iconKey: 'ac' },
  'air conditioning': { label: 'Air conditioning', iconKey: 'ac' },
  geothermal: { label: 'Geothermal heat', iconKey: 'fire' },
  caretaker: { label: 'Caretaker', iconKey: 'person' },
  gas: { label: 'Gas included', iconKey: 'gas' },
  'hot water': { label: 'Hot water incl.', iconKey: 'droplet' },
  clubhouse: { label: 'Meeting/Party Room', iconKey: 'sofa' },
  'club house': { label: 'Meeting/Party Room', iconKey: 'sofa' },
  concierge: { label: 'Concierge', iconKey: 'person' },
  'storage - locker': { label: 'Storage locker', iconKey: 'box' },
  'party room': { label: 'Party room', iconKey: 'sofa' },
  'laundry - in suite': { label: 'In-suite laundry', iconKey: 'wash' },
  'shared laundry': { label: 'Shared laundry', iconKey: 'wash' },
  'security/concierge': { label: 'Security / concierge', iconKey: 'person' },
  'visitor parking': { label: 'Visitor parking', iconKey: 'car' },
  'recreation centre': { label: 'Recreation centre', iconKey: 'gym' },
  'recreation center': { label: 'Recreation centre', iconKey: 'gym' },
  sauna: { label: 'Sauna', iconKey: 'fire' },
  'guest suite': { label: 'Guest suite', iconKey: 'bed' },
  'car wash': { label: 'Car wash', iconKey: 'spray' },
};

/** Map raw amenity strings → pills, deduped and minus anything already a highlight. */
export function resolveAmenities(
  associationAmenities: string[] | null,
  extractedFeatures: string[],
): AmenityItem[] {
  const highlightLabels = new Set(extractedFeatures.map((s) => s.toLowerCase()));
  return (associationAmenities ?? [])
    .map((a) => AMENITY_MAP[a.toLowerCase()])
    .filter((a): a is AmenityItem => !!a)
    .filter((a, i, arr) => arr.findIndex((b) => b.label === a.label) === i)
    .filter((a) => !highlightLabels.has(a.label.toLowerCase()));
}

/* ───────────── 'About this home' redesign derivations (VOW deep dives) ─────────────
 * Merged from the redesign handoff. Pure, no JSX/MUI. Reads new VOW fields off
 * ListingDetail (rooms[], finished-area split, construction, lot/legal).
 */
export interface RoomRow {
  name: string; // e.g. "Living Room"
  dim: string; // e.g. "14'8 × 18'6"
}
export interface RoomGroup {
  level: string; // "Main floor" | "Basement" | ...
  count: string; // "7 rooms"
  sqft: string | null; // "1,360 ft²" or null
  items: RoomRow[];
}

const LEVEL_LABEL: Record<string, string> = {
  main: 'Main floor',
  bsmt: 'Basement',
  basement: 'Basement',
  abve: 'Upper floor',
  above: 'Upper floor',
};

function levelLabel(raw: string): string {
  return LEVEL_LABEL[raw.trim().toLowerCase()] ?? raw;
}

/** Group rooms by level, preserving feed order of levels. */
export function buildRoomGroups(l: ListingDetail): RoomGroup[] {
  const rooms = (l as any).rooms as
    | Array<{ type: string; level: string; length: string; width: string }>
    | undefined;
  if (!rooms || rooms.length === 0) return [];

  const order: string[] = [];
  const byLevel = new Map<string, RoomRow[]>();
  for (const r of rooms) {
    const lvl = levelLabel(r.level);
    if (!byLevel.has(lvl)) {
      byLevel.set(lvl, []);
      order.push(lvl);
    }
    byLevel.get(lvl)!.push({
      name: r.type,
      dim: `${r.length} × ${r.width}`,
    });
  }

  // Per-level finished sqft anchor (above grade = main+upper, below = basement)
  const areaForLevel = (lvl: string): string | null => {
    if (/basement/i.test(lvl) && l.belowGradeFinishedArea != null)
      return `${Math.round(l.belowGradeFinishedArea).toLocaleString('en-CA')} ft²`;
    if (/main|upper/i.test(lvl) && (l as any).aboveGradeFinishedArea != null)
      return `${Math.round((l as any).aboveGradeFinishedArea).toLocaleString('en-CA')} ft²`;
    return null;
  };

  return order.map((lvl) => {
    const items = byLevel.get(lvl)!;
    return {
      level: lvl,
      count: `${items.length} room${items.length === 1 ? '' : 's'}`,
      sqft: areaForLevel(lvl),
      items,
    };
  });
}

export interface AreaSplit {
  total: string; // "2,584"
  segments: Array<{ label: string; value: string; pct: number; key: 'a' | 'b' }>;
  unfinished: string | null;
}

/** Above-grade vs below-grade finished split + total. */
export function buildFinishedAreaSplit(l: ListingDetail): AreaSplit | null {
  const above = (l as any).aboveGradeFinishedArea as number | null;
  const below = (l as any).belowGradeFinishedArea as number | null;
  const total = (l as any).livingAreaFinishedTotal ?? l.livingArea ?? null;
  if (above == null && below == null) return null;

  const a = above ?? 0;
  const b = below ?? 0;
  const sum = a + b || (total ?? 0);
  if (sum <= 0) return null;

  const segments: AreaSplit['segments'] = [];
  if (a > 0)
    segments.push({
      label: 'Main floor',
      value: `${a.toLocaleString('en-CA')} ft²`,
      pct: Math.round((a / sum) * 100),
      key: 'a',
    });
  if (b > 0)
    segments.push({
      label: 'Basement',
      value: `${b.toLocaleString('en-CA')} ft²`,
      pct: Math.round((b / sum) * 100),
      key: 'b',
    });

  return {
    total: Math.round(total ?? sum).toLocaleString('en-CA'),
    segments,
    unfinished: '0 ft²', // map from TotalFloorUnfinishedArea when > 0
  };
}

export interface FactRow {
  label: string;
  value: string;
}

/**
 * Construction & systems. Returns { pub, vow }: `pub` = already on the public
 * listing (Heating is shown today via buildFeatureGroups). `vow` = member-only.
 * Caller renders `vow` rows behind the gate.
 */
export function buildBuildingFacts(l: ListingDetail): { pub: FactRow[]; vow: FactRow[] } {
  const join = (a: string[] | null | undefined) =>
    (a ?? []).filter(Boolean).join(' · ') || null;

  const pub: FactRow[] = [];
  if (join(l.heating)) pub.push({ label: 'Heating', value: join(l.heating)! });

  const vow: (FactRow | null)[] = [
    join(l.constructionMaterials) && { label: 'Construction', value: join(l.constructionMaterials)! },
    join((l as any).foundationDetails) && { label: 'Foundation', value: join((l as any).foundationDetails)! },
    join(l.roof) && { label: 'Roof', value: join(l.roof)! },
    join(l.flooring) && { label: 'Flooring', value: join(l.flooring)! },
    (l as any).waterSource && { label: 'Water', value: (l as any).waterSource },
    join((l as any).sewer) && { label: 'Sewer', value: join((l as any).sewer)! },
    join((l as any).utilities) && { label: 'Utilities', value: join((l as any).utilities)! },
  ];

  return { pub, vow: vow.filter(Boolean) as FactRow[] };
}

/** PID 014543818 -> 014-543-818 */
export function formatPid(parcel: string | null): string | null {
  if (!parcel) return null;
  const d = parcel.replace(/\D/g, '');
  return d.length === 9 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : parcel;
}

export interface LotLegal {
  zoning: string | null; // VOW
  lotSize: string | null; // PUBLIC (already shown)
  lotDims: string | null; // PUBLIC ("50 × 219.55 · lane access")
  vowRows: FactRow[]; // rear-yard faces, PID
  taxYear: string | null; // PUBLIC
  legal: string | null; // VOW
}

export function buildLotLegal(l: ListingDetail): LotLegal {
  const lotSize =
    l.lotSizeSquareFeet != null && l.lotSizeSquareFeet > 0
      ? formatLotSize(l.lotSizeSquareFeet)
      : null;
  const front = l.frontageLength != null && l.frontageLength > 0 ? l.frontageLength : null;
  const depth = (l as any).lotSizeDepth ?? null;
  const lotDims = front && depth ? `${front} × ${depth}` : null;

  const vowRows: FactRow[] = [
    (l as any).directionFacesRearYard && { label: 'Rear yard faces', value: (l as any).directionFacesRearYard },
    formatPid(l.parcelNumber) && { label: 'Parcel ID (PID)', value: formatPid(l.parcelNumber)! },
  ].filter(Boolean) as FactRow[];

  return {
    zoning: (l as any).zoning ?? null,
    lotSize,
    lotDims,
    vowRows,
    taxYear: l.taxYear != null ? String(l.taxYear) : null,
    legal: (l as any).taxLegalDescription ?? null,
  };
}
